import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIventisDroppable } from "@iventis/drag-and-drop";
import { borderRadius, inlineTextIconMargin, popoverPadding, fontSizes, iconButtonSize, spatialHeaderIconSize } from "@iventis/styles/src/atomic-rules";
import { InteractiveElement } from "@iventis/styles/src/components";
import { Theme } from "@emotion/react";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Popover, PopoverOrigin } from "@mui/material";
import React, { Ref, forwardRef, useMemo, useRef, useState, MouseEvent, Fragment, PropsWithChildren } from "react";
import { styled } from "@iventis/styles";
import useHideOverflowContent from "@iventis/utilities/src/hooks/use-hide-overflow-content";
import ListComponent from "./list";

export interface BreadcrumbElement {
    id: string;
    name: string;
}

interface BreadcrumbContainerProps<TBreadcrumb extends BreadcrumbElement = BreadcrumbElement> {
    breadcrumbs: TBreadcrumb[];
    headerIcon: IconProp;
    onElementClick: (element: TBreadcrumb) => void;
    className?: string;
    popOverOrigin?: { anchorOrigin: PopoverOrigin; transformOrigin: PopoverOrigin };
    droppable?: boolean;
    translatableContent?: Record<string, string>;
}

interface BreadcrumbProps<TBreadcrumb extends BreadcrumbElement = BreadcrumbElement> extends BreadcrumbContainerProps<TBreadcrumb> {
    breadcrumbs: (TBreadcrumb & { hidden: boolean })[];
}

const BREADCRUMB_HEIGHT = "44px";

export function BreadCrumbComponent<TBreadcrumb extends BreadcrumbElement = BreadcrumbElement>({
    breadcrumbs,
    onElementClick,
    headerIcon,
    className,
    popOverOrigin,
    droppable = true,
    translatableContent,
}: PropsWithChildren<BreadcrumbContainerProps<TBreadcrumb>>) {
    const containerRef = useRef<HTMLDivElement>(null);
    const localBreadcrumbs = useHideOverflowContent(breadcrumbs, containerRef);
    return (
        <BreadcrumbsContainer ref={containerRef} className={className}>
            <Breadcrumbs<TBreadcrumb>
                breadcrumbs={localBreadcrumbs}
                onElementClick={onElementClick}
                translatableContent={translatableContent}
                headerIcon={headerIcon}
                popOverOrigin={popOverOrigin}
                droppable={droppable}
            />
        </BreadcrumbsContainer>
    );
}

function Breadcrumbs<TBreadcrumb extends BreadcrumbElement = BreadcrumbElement>({
    breadcrumbs,
    onElementClick,
    headerIcon,
    popOverOrigin,
    droppable = true,
    translatableContent,
}: PropsWithChildren<BreadcrumbProps<TBreadcrumb>>) {
    const breadcrumbsToShow = useMemo(() => breadcrumbs.filter((breadcrumb) => !breadcrumb.hidden), [JSON.stringify(breadcrumbs)]);
    const [popoverAnchor, setPopoverAnchor] = useState<Element>(null);
    const translate = useIventisTranslate();

    const handleHiddenBreadcrumbClick = (event: MouseEvent<HTMLButtonElement>) => {
        setPopoverAnchor(event.currentTarget);
        event.stopPropagation();
    };

    return (
        <>
            {breadcrumbsToShow.map((crumb, index) => (
                <Fragment key={crumb.id}>
                    <Breadcrumb breadcrumb={crumb} className="breadcrumb" droppable={droppable}>
                        {index === 0 && (
                            <div className="header-icon-container">
                                <FontAwesomeIcon icon={headerIcon} className="header-icon" />
                            </div>
                        )}
                        <InteractiveElement
                            data-testid={`breadcrumb-node-${translatableContent?.[crumb.name] ? translatableContent[crumb.name] : crumb.name}`}
                            className="crumb-name"
                            onClick={() => onElementClick(crumb)}
                        >
                            {translatableContent?.[crumb.name] ? <span>{translate(translatableContent[crumb.name])}</span> : <span>{crumb.name}</span>}
                        </InteractiveElement>
                    </Breadcrumb>
                    {index === 0 && breadcrumbs.some((breadcrumb) => breadcrumb.hidden) && (
                        <StyledBreadcrumb>
                            <FontAwesomeIcon className="chevron" size="sm" icon={{ prefix: "far", iconName: "chevron-right" }} />
                            <InteractiveElement onClick={handleHiddenBreadcrumbClick}>
                                <FontAwesomeIcon icon={{ prefix: "far", iconName: "folders" }} />
                            </InteractiveElement>
                        </StyledBreadcrumb>
                    )}
                    {index !== breadcrumbsToShow.length - 1 && <FontAwesomeIcon className="chevron" size="sm" icon={{ prefix: "far", iconName: "chevron-right" }} />}
                </Fragment>
            ))}
            <StyledPopover
                anchorEl={popoverAnchor}
                anchorOrigin={popOverOrigin?.anchorOrigin}
                transformOrigin={popOverOrigin?.transformOrigin}
                open={popoverAnchor != null}
                onClose={() => setPopoverAnchor(null)}
            >
                <ListComponent
                    listItems={breadcrumbs.map((crumb, index) => ({ ...crumb, icon: index === 0 ? headerIcon : null }))}
                    onButtonClick={(item) => {
                        setPopoverAnchor(null);
                        onElementClick(item);
                    }}
                    className="folder-list"
                />
            </StyledPopover>
        </>
    );
}

function Breadcrumb<TBreadcrumb extends BreadcrumbElement = BreadcrumbElement>({
    breadcrumb,
    className,
    droppable = true,
    children,
}: PropsWithChildren<{
    breadcrumb: TBreadcrumb;
    className: string;
    droppable: boolean;
}>) {
    return droppable ? (
        <DroppableBreadcrumb breadcrumb={breadcrumb} className={className}>
            {children}
        </DroppableBreadcrumb>
    ) : (
        <NormalBreadcrumb className={className}>{children}</NormalBreadcrumb>
    );
}

function DroppableBreadcrumb<TBreadcrumb extends BreadcrumbElement = BreadcrumbElement>({
    breadcrumb,
    className,
    children,
}: PropsWithChildren<{
    breadcrumb: TBreadcrumb;
    className: string;
}>) {
    const { setNodeRef } = useIventisDroppable({ id: breadcrumb.id }, { suffix: "breadcrumb" });
    return (
        <StyledBreadcrumb className={className} ref={(el) => setNodeRef(el)}>
            {children}
        </StyledBreadcrumb>
    );
}

const NormalBreadcrumb = forwardRef(({ className, children }: PropsWithChildren<{ className: string }>, ref) => (
    <StyledBreadcrumb ref={ref as Ref<HTMLDivElement>} className={className}>
        {children}
    </StyledBreadcrumb>
));

const BreadcrumbsContainer = styled.div`
    height: ${BREADCRUMB_HEIGHT};

    display: flex;
    align-items: center;
    justify-content: flex-start;
    white-space: nowrap;

    .chevron {
        color: ${({ theme }: { theme: Theme }) => theme.shades.one};
    }

    .header-icon {
        width: ${spatialHeaderIconSize.width};
        height: ${spatialHeaderIconSize.height};
    }

    .crumb-name {
        color: ${({ theme }: { theme: Theme }) => theme.shades.one};
        font-size: ${fontSizes.medium};
    }

    .breadcrumb:last-child {
        .crumb-name {
            font-weight: bold;
        }
    }

    .header-icon-container {
        width: ${iconButtonSize};
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;

const StyledPopover = styled(Popover)`
    .folder-list {
        button {
            display: flex;
            justify-content: flex-start;
            align-items: center;
            width: 100%;
            padding: ${popoverPadding.small};
            gap: ${inlineTextIconMargin};
            border-radius: ${borderRadius.standard};
        }

        span {
            color: ${({ theme }: { theme: Theme }) => theme.typographyColors.core};
            white-space: nowrap;
            text-overflow: ellipsis;
        }

        button:hover {
            background-color: ${({ theme }: { theme: Theme }) => theme.shades.four};
        }

        button:last-child {
            span {
                font-weight: bold;
            }
        }
    }
`;

const StyledBreadcrumb = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;

    padding: 0 10px;

    position: relative;

    :first-child {
        padding-left: 0;
    }
`;
